/**
 * Unlayer's social tool always renders every icon, even when the href is
 * empty or still an unresolved merge tag (`{{ instagram_url }}`). Shared
 * email templates therefore show a dead Instagram (etc.) button at clinics
 * that left that URL blank.
 *
 * This strips those icons from exported HTML so the clinic can stay linked
 * to the group design. Only Unlayer social-tool markup is touched
 * (`cdn.tools.unlayer.com/social`); other empty links are left alone.
 *
 * Keep in sync with the server and Firebase copies:
 * - server/src/db/helpers/email-templates.helpers/hide-empty-social-icons.ts
 * - server/firebase/functions/src/utils/hide-empty-social-icons.ts
 */

const UNLAYER_SOCIAL_ICON_SRC = 'cdn.tools.unlayer.com/social'

/** Outlook wrapper Unlayer emits immediately before each social icon table. */
const MSO_TD_OPEN =
    /<!--\[if \(mso\)\|\(IE\)\]><td[^>]*><!\[endif\]-->\s*$/i
/** Outlook wrapper Unlayer emits immediately after each social icon table. */
const MSO_TD_CLOSE = /^\s*<!--\[if \(mso\)\|\(IE\)\]><\/td><!\[endif\]-->/i

type TableRange = {
    start: number
    end: number
}

export function hideEmptySocialIcons(html: string): string {
    if (!html || !html.includes(UNLAYER_SOCIAL_ICON_SRC)) {
        return html
    }

    const tables = findInnermostTablesContaining(html, UNLAYER_SOCIAL_ICON_SRC)
    let result = html

    for (let i = tables.length - 1; i >= 0; i--) {
        const table = tables[i]
        const tableHtml = result.slice(table.start, table.end)
        if (!isEmptySocialHref(getAnchorHref(tableHtml))) continue

        const { start, end } = expandMsoWrapper(result, table.start, table.end)
        result = result.slice(0, start) + result.slice(end)
    }

    return result
}

function findInnermostTablesContaining(
    html: string,
    needle: string,
): TableRange[] {
    const events = collectTableEvents(html)
    const stack: number[] = []
    const ranges: TableRange[] = []

    for (const event of events) {
        if (event.type === 'open') {
            stack.push(event.index)
            continue
        }
        const start = stack.pop()
        if (start === undefined) continue

        const end = event.index + event.length
        const tableHtml = html.slice(start, end)
        if (!tableHtml.includes(needle)) continue
        if (!isInnermostTable(tableHtml)) continue

        ranges.push({ start, end })
    }

    return ranges
}

type TableEvent = {
    index: number
    type: 'open' | 'close'
    length: number
}

function collectTableEvents(html: string): TableEvent[] {
    const events: TableEvent[] = []
    const tableOpen = /<table\b/gi
    const tableClose = /<\/table>/gi

    let match: RegExpExecArray | null
    while ((match = tableOpen.exec(html))) {
        events.push({
            index: match.index,
            type: 'open',
            length: match[0].length,
        })
    }
    while ((match = tableClose.exec(html))) {
        events.push({
            index: match.index,
            type: 'close',
            length: match[0].length,
        })
    }

    return events.sort((a, b) => a.index - b.index)
}

/** True when this `<table>` does not contain another `<table>`. */
function isInnermostTable(tableHtml: string): boolean {
    const innerStart = tableHtml.indexOf('>') + 1
    const innerEnd = tableHtml.lastIndexOf('</table>')
    if (innerStart <= 0 || innerEnd <= innerStart) return false
    return !/<table\b/i.test(tableHtml.slice(innerStart, innerEnd))
}

function expandMsoWrapper(
    html: string,
    start: number,
    end: number,
): TableRange {
    const before = html.slice(0, start)
    const after = html.slice(end)
    const openMatch = before.match(MSO_TD_OPEN)
    const closeMatch = after.match(MSO_TD_CLOSE)

    return {
        start: openMatch ? start - openMatch[0].length : start,
        end: closeMatch ? end + closeMatch[0].length : end,
    }
}

function getAnchorHref(tableHtml: string): string | undefined {
    const match = tableHtml.match(
        /<a\b[^>]*\bhref\s*=\s*(["'])([\s\S]*?)\1/i,
    )
    if (!match) return undefined
    return decodeBasicHtmlEntities(match[2]).trim()
}

/**
 * A social icon href is unusable when it is blank, a placeholder, or still
 * an unresolved `{{ merge_tag }}` (clinic never set the URL, or the value
 * was empty so some replace paths leave the tag in place).
 */
export function isEmptySocialHref(href: string | undefined): boolean {
    if (href == null) return true

    const value = href.trim()
    if (!value) return true
    if (value === '#' || value === '/') return true
    if (/^javascript:/i.test(value)) return true
    if (/^about:blank$/i.test(value)) return true
    if (/^https?:\/\/$/i.test(value)) return true
    if (/^\{\{\s*[\w.-]+\s*\}\}$/.test(value)) return true

    return false
}

function decodeBasicHtmlEntities(value: string): string {
    return value
        .replace(/&amp;/g, '&')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&nbsp;/g, ' ')
}
